import React, { useState } from "react"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { BulkPlanFinding } from "../../../config/bulkResultsInsights.config"
import type { IBulkResultsFindingStat } from "../../../types/bulkResultsInsights.types"

export const BulkResultsPlanFindingsComponent: React.FunctionComponent<
    IBulkResultsPlanFindingsComponentProps
> = props => {
    const [expandedFinding, setExpandedFinding] = useState<BulkPlanFinding>()

    return (
        <div className={props.className}>
            <div className="mb-[16px] flex items-baseline gap-[12px]">
                <PrimaryText typography="h5">Plan findings</PrimaryText>

                <PrimaryText typography="subtitle" className="opacity-60">
                    Patients can appear in more than one finding
                </PrimaryText>
            </div>

            <div className="grid grid-cols-5 items-start gap-[12px] lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1">
                {props.findings.map(finding => {
                    const isSelected = props.selectedFinding === finding.key
                    const isExpanded = expandedFinding === finding.key
                    const isWarning = finding.isWarning

                    return (
                        <div
                            key={finding.key}
                            className={`
                                rounded-[16px] border border-solid p-[20px] shadow-sm
                                ${
                                    isWarning
                                        ? "border-brand-paleRed2 bg-brand-paleRed"
                                        : "bg-brand-white"
                                }
                                ${
                                    isSelected && !isWarning
                                        ? "border-brand-primary"
                                        : !isWarning
                                          ? "border-transparent"
                                          : ""
                                }
                            `}
                        >
                            <button
                                type="button"
                                className="w-full border-0 bg-transparent p-0 text-left"
                                onClick={() => {
                                    props.onSelect(finding.key, finding.label)
                                }}
                            >
                                <div className="mb-[12px] flex items-center gap-[8px]">
                                    <FontAwesomeIcon
                                        icon={finding.icon}
                                        className={
                                            isWarning
                                                ? "text-brand-redDark"
                                                : "text-brand-primary"
                                        }
                                    />

                                    <PrimaryText weight="medium">
                                        {finding.label}
                                    </PrimaryText>
                                </div>

                                <PrimaryText
                                    typography="h3"
                                    color={
                                        isWarning
                                            ? NIRVANA_COLORS.brand.redDark
                                            : undefined
                                    }
                                >
                                    {finding.count}
                                </PrimaryText>

                                <PrimaryText
                                    className="mt-[4px]"
                                    color={
                                        isWarning
                                            ? NIRVANA_COLORS.brand.redDark
                                            : undefined
                                    }
                                >
                                    patients flagged
                                </PrimaryText>
                            </button>

                            {finding.breakdown.length ? (
                                <button
                                    type="button"
                                    className="mt-[12px] border-0 bg-transparent p-0 text-brand-vibrantPurple"
                                    onClick={event => {
                                        event.stopPropagation()
                                        setExpandedFinding(current =>
                                            current === finding.key
                                                ? undefined
                                                : finding.key,
                                        )
                                    }}
                                >
                                    <PrimaryText
                                        weight="medium"
                                        color="inherit"
                                    >
                                        {isExpanded
                                            ? "Hide breakdown"
                                            : "View breakdown"}
                                    </PrimaryText>
                                </button>
                            ) : null}

                            {isExpanded ? (
                                <div className="mt-[12px] border-0 border-t border-solid border-brand-warmLight2 pt-[12px]">
                                    {finding.breakdown.map(item => (
                                        <div
                                            key={item.key}
                                            className="mb-[8px] flex items-center justify-between last:mb-0"
                                        >
                                            <PrimaryText>
                                                {item.label}
                                            </PrimaryText>

                                            <PrimaryText weight="medium">
                                                {item.count}
                                            </PrimaryText>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

interface IBulkResultsPlanFindingsComponentProps {
    className?: string
    findings: IBulkResultsFindingStat[]
    selectedFinding?: BulkPlanFinding
    onSelect: (finding: BulkPlanFinding, label: string) => void
}
